import { ComponentProps, ReactNode, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Merge } from "type-fest";

import { IUser, IUserResource } from "@sol/sdk";
import { Button } from "@sol/uikit/general/Button/Button";

import { UserPickerForm } from "./UserPickerForm";
import { UserPickerList } from "./UserPickerList";
import ApiErrorAlert from "../../../components/ApiErrorAlert";

type Props = Merge<
    {
        className?: string;
        title: string;
        subtitle?: ReactNode | string;
        onUserAdd: (user: IUser) => void;
        showEmailOnly?: boolean;
        emailCheckingApiError?: string;
        emailCheckingLoading?: boolean;
        users: IUserResource[];
        usersListTitle: string;
        noUserMessage: string;
        onUserRemove: (email: string) => void;
        isLoading: boolean;
        onClose: () => void;
        onSubmit: () => void;
        isSubmitting: boolean;
        disabledSubmit?: boolean;
        apiErrorsReset?: () => void;
        apiErrors?: any;
    },
    Omit<ComponentProps<"div">, "title" | "onSubmit">
>;

export const UserPicker = ({
    className,
    title,
    subtitle,
    onUserAdd,
    showEmailOnly,
    emailCheckingApiError,
    emailCheckingLoading,
    users,
    usersListTitle,
    noUserMessage,
    onUserRemove,
    isLoading,
    onClose,
    onSubmit,
    isSubmitting,
    disabledSubmit,
    apiErrorsReset,
    apiErrors,
    ...divProps
}: Props) => {
    const [t] = useTranslation();
    const submitButtonRef = useRef(null);

    return (
        <div {...divProps} className={className}>
            <div className="grid grid-cols-12 divide-x divide-grey-lightest p-4">
                <div className="col-span-6 pr-12">
                    <UserPickerForm
                        title={title}
                        subtitle={subtitle}
                        onUserAdd={onUserAdd}
                        showEmailOnly={showEmailOnly}
                        apiErrorMessage={emailCheckingApiError}
                        isLoading={emailCheckingLoading}
                    />
                </div>
                <div className="col-span-6 pl-12">
                    <UserPickerList
                        users={users}
                        title={usersListTitle}
                        noUserMessage={noUserMessage}
                        onUserRemove={onUserRemove}
                        isLoading={isLoading}
                        footer={
                            <div className="flex items-center justify-end gap-4">
                                <Button data-cy="close-modal" onClick={onClose} disabled={isSubmitting}>
                                    {t("component.TutorPicker.cancel")}
                                </Button>
                                <Button
                                    data-cy="user-picker-submit"
                                    type="primary"
                                    onClick={onSubmit}
                                    loading={isSubmitting}
                                    disabled={users.length === 0}
                                >
                                    {t("component.TutorPicker.submit")}
                                </Button>
                            </div>
                        }
                    />
                </div>
            </div>
            {apiErrors && apiErrorsReset && (
                <ApiErrorAlert error={apiErrors} dissmiss={apiErrorsReset} triggerElement={submitButtonRef} />
            )}
        </div>
    );
};
